import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Filter, Search, History } from "lucide-react";
import TablePagination from "@/components/masters/TablePagination";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { activityLogApi } from "@/services/api";
import { SingleSelect } from "@/components/ui/single-select";
import api from "@/services/api";

// Enums for action and module
export enum ActionType {
  create = "create",
  update = "update",
  delete = "delete",
}

export enum Module {
  account = "account",
  area = "area",
  category = "category",
  division = "division",
  group = "group",
  zone = "zone",
  collection = "collection",
  consumer = "consumer",
  organization = "organization",
  payment = "payment",
  service = "service",
  user = "user",
  voucher = "voucher",
}

interface UserOption {
  id: string;
  name: string;
}

// Define the log type based on API response
interface ActivityLog {
  id: string;
  organizationId: string;
  doneById: string;
  module: string;
  deviceId?: string | null;
  description: string;
  action: string;
  serverTime: string;
  hostTime: string;
  createdAt: string;
  updatedAt: string;
  doneBy?: {
    id: string;
    name: string;
    image?: string;
    mobile?: string;
  };
}

const LogsPage = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    userId: "all",
    module: "all",
    action: "all",
  });
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [userOptions, setUserOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // Fetch users for dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.user.getUsers({ limit: 1000 });
        const userArr = Array.isArray(res.data.docs) ? res.data.docs : [];
        setUsers(userArr);
        setUserOptions(
          userArr.map((user: UserOption) => ({
            label: user.name,
            value: user.id,
          }))
        );
      } catch (e) {
        setUsers([]);
        setUserOptions([]);
      }
    };
    fetchUsers();
  }, []);

  // Fetch logs from API
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (filters.userId !== "all") params.userId = filters.userId;
      if (filters.module !== "all") params.module = filters.module;
      if (filters.action !== "all") params.action = filters.action;
      const res = await api.activityLog.getActivityLogs(params);
      setLogs(res.docs || []);
      setTotalLogs(res.totalDocs || 0);
    } catch (error) {
      setLogs([]);
      setTotalLogs(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filters]);

  // Handle filter change
  const handleFilterChange = (field: string, value: string) => {
    setFilters({ ...filters, [field]: value });
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      userId: "all",
      module: "all",
      action: "all",
    });
    setCurrentPage(1);
  };

  // Get user name from log
  const getUserName = (log: ActivityLog) => {
    return log.doneBy?.name || "Unknown User";
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return format(date, "MMM dd, yyyy HH:mm:ss");
    } catch (error) {
      return timestamp;
    }
  };

  // Module and action options from enums
  const moduleOptions = [
    { label: "All Modules", value: "all" },
    ...Object.values(Module).map((mod) => ({
      label: mod.charAt(0).toUpperCase() + mod.slice(1),
      value: mod,
    })),
  ];
  const actionOptions = [
    { label: "All Actions", value: "all" },
    ...Object.values(ActionType).map((act) => ({
      label: act.charAt(0).toUpperCase() + act.slice(1),
      value: act,
    })),
  ];

  const totalPages = Math.ceil(totalLogs / itemsPerPage);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Activity Logs</h1>
        <p className="text-muted-foreground">
          Track changes and activities across the platform
        </p>
      </div>

      <div className="w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-end justify-center">
          {/* User dropdown (SingleSelect) */}
          <div>
            <SingleSelect
              options={[...userOptions]}
              selected={filters.userId !== "all" ? [filters.userId] : []}
              onChange={(selected) => {
                const value = selected.length > 0 ? selected[0] : "all";
                handleFilterChange("userId", value);
              }}
              placeholder="All Users"
              className="w-full"
            />
          </div>
          {/* Module dropdown (normal Select) */}
          <div>
            <Select
              value={filters.module}
              onValueChange={(value) => handleFilterChange("module", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                {moduleOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Action dropdown (normal Select) */}
          <div>
            <Select
              value={filters.action}
              onValueChange={(value) => handleFilterChange("action", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                {actionOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Placeholder for spacing or future filter */}
          <div></div>
          {/* Reset button */}
          <div>
            <Button variant="outline" onClick={resetFilters} className="w-full">
              Reset
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2"></CardHeader>
        <CardContent>
          <div className="overflow-x-auto w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Server Time</TableHead>
                  <TableHead>Host Time</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-muted-foreground"
                    >
                      {filters.userId !== "all" ||
                      filters.module !== "all" ||
                      filters.action !== "all"
                        ? "No logs match your search criteria"
                        : "No activity logs found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">
                        {getUserName(log)}
                      </TableCell>
                      <TableCell>{log.module}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            log.action === "create"
                              ? "bg-green-100 text-green-800 hover:bg-green-100"
                              : log.action === "update"
                              ? "bg-blue-100 text-blue-800 hover:bg-blue-100"
                              : "bg-red-100 text-red-800 hover:bg-red-100"
                          }
                          variant="outline"
                        >
                          {log.action.charAt(0).toUpperCase() +
                            log.action.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{formatTimestamp(log.serverTime)}</span>
                          <span className="text-xs text-muted-foreground">
                            Server
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{formatTimestamp(log.hostTime)}</span>
                          <span className="text-xs text-muted-foreground">
                            Host
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[300px] break-words whitespace-pre-line">
                        {log.description}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
            {totalLogs > itemsPerPage && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
            <div className="flex items-center gap-2">
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setCurrentPage(1);
                  setItemsPerPage(parseInt(value));
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">per page</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LogsPage;
